// Two DIFFERENT concepts live in this file. Keeping them apart matters.
//
// 1. MACHINERY_HEADS — the values `machinery_norms.category` may take. This is a
//    rate-card discriminator: every head has the same row shape (Activity + Norm
//    Code + Description -> one Rate). MUCKING is the only one. The backend
//    validates writes against its own copy of this list
//    (src/machinery-norms/machinery-heads.constant.ts).
//
// 2. MACHINERY_TABS — what the Machine page shows. These are NOT heads. Two of
//    them are separate entities with their own tables, APIs and columns, so
//    sending a tab key as a `category` would be rejected by the backend's head
//    guard.
//
// HISTORY, so nobody re-adds them: this file used to carry eight further heads —
// DEPRECIATION, INTEREST, REPAIR, MISC, INSURANCE, ELECTRICITY, FUEL and
// LUBRICANTS — each rendering an "upload format coming soon" placeholder. They
// were removed once the source workbooks arrived, because those eight are not
// upload formats at all: they are COMPUTED OUTPUT COLUMNS of the Equipment
// Hourly Use Rate master, derived from ~24 engineering inputs per machine. If a
// genuinely new rate-card head appears (same flat Activity+Code shape), add it
// to MACHINERY_HEADS here AND to the backend constant. Anything that computes
// its numbers belongs in machinery-equipment instead.

export interface MachineryHead {
  key: string;
  label: string;
  /** Kept so a future head can be registered before its format is wired. */
  live: boolean;
}

export const MACHINERY_HEADS: MachineryHead[] = [
  { key: "MUCKING", label: "Mucking", live: true },
];

/** Heads that accept data. Identical to MACHINERY_HEADS today, by construction. */
export const LIVE_MACHINERY_HEADS = MACHINERY_HEADS.filter((h) => h.live);

export const DEFAULT_MACHINERY_HEAD = "MUCKING";

export const isMachineryHeadLive = (key?: string): boolean =>
  MACHINERY_HEADS.some((h) => h.key === key && h.live);

export const machineryHeadLabel = (key?: string): string =>
  MACHINERY_HEADS.find((h) => h.key === key)?.label ?? key ?? "";

/**
 * With one head there is nothing to pick. Surfaces that offer a head selector
 * (the apply modal, the bulk-apply upload, the cost-analysis filter) use this to
 * hide a control that would show a single option.
 */
export const hasMultipleMachineryHeads = (): boolean => MACHINERY_HEADS.length > 1;

// ---------------------------------------------------------------- page tabs

export type MachineryTabKey = "MUCKING" | "EQUIPMENT_RATES" | "DEPLOYMENT_NORMS";

export interface MachineryTab {
  key: MachineryTabKey;
  label: string;
  /**
   * 'norm-category' -> a machinery_norms head; its key IS a valid `category`.
   * 'entity'        -> a separate master with its own API. NEVER send its key
   *                    as a category.
   */
  kind: "norm-category" | "entity";
}

export const MACHINERY_TABS: MachineryTab[] = [
  { key: "MUCKING", label: "Mucking", kind: "norm-category" },
  { key: "EQUIPMENT_RATES", label: "Equipment Rates", kind: "entity" },
  { key: "DEPLOYMENT_NORMS", label: "Equipment Norms", kind: "entity" },
];

export const DEFAULT_MACHINERY_TAB: MachineryTabKey = "MUCKING";

/** True when the tab maps onto a machinery_norms category. */
export const isNormCategoryTab = (key: string): boolean =>
  MACHINERY_TABS.some((t) => t.key === key && t.kind === "norm-category");
